import { isOnlyParam, isOnlyParamForHa } from '@doda/common/src/utils/seoUtil';
import { JobCountQuery } from '@doda/common/src/services/getJobCountAPI';
import {
  IndustryLResponse,
  JobTypeLResponse,
} from '@doda/api-client/aspida/@types';
import { createAreasApiRequest } from '@doda/common/src/logics/JobContent/apiRequest/createAreasApiRequest';
import { createPrefectureApiRequest } from '@doda/common/src/logics/JobContent/apiRequest/createPrefectureApiRequest';
import { createCitiesApiRequest } from '@doda/common/src/logics/JobContent/apiRequest/createCitiesApiRequest';
import { createIndustryApiRequest } from '@doda/common/src/logics/JobContent/apiRequest/createIndustryApiRequest';
import { createOccupationApiRequest } from '@doda/common/src/logics/JobContent/apiRequest/createOccupationApiRequest';
import { createOptionApiRequest } from '@doda/common/src/logics/JobContent/apiRequest/createOptionApiRequest';
import { createLowerLimitIncomeApiRequest } from '@doda/common/src/logics/JobContent/apiRequest/createLowerLimitIncomeApiRequest';
import { createEmployNumApiRequest } from '@doda/common/src/logics/JobContent/apiRequest/createEmployNumApiRequest';
import { createEstablishApiRequest } from '@doda/common/src/logics/JobContent/apiRequest/createEstablishApiRequest';
import { AppQueryParams } from '@doda/common/src/constants/queryParams';
import {
  AreaListOfSearchModal,
  CityOfSearchModal,
} from '@doda/common/src/models/MasterCacheModel';

interface CreateNewRequestParams {
  /** そのページのクエリパラメータ */
  query: AppQueryParams;
  /** areaのマスタキャッシュ*/
  areas: AreaListOfSearchModal[] | null;
  /** 市区町村のマスタキャッシュ*/
  cities: CityOfSearchModal[] | null;
  /** 職種のマスタキャッシュ*/
  occupations: JobTypeLResponse[] | null;
  /** 業種のマスタキャッシュ*/
  industries: IndustryLResponse[] | null;
}

/**
 * api叩くための値作成処理
 * @param CreateNewRequestParams
 * @returns リクエストパラメータとそのページの一軸名
 */
export const createNewRequestParamsForApi = ({
  query,
  areas,
  cities,
  occupations,
  industries,
}: CreateNewRequestParams) => {
  // パラメータ該当名
  let targetName = '';
  // API呼び出し引数用変数
  const requestParams: JobCountQuery = {};
  if (
    isOnlyParam(
      query.ar,
      query.pr,
      query.ci,
      query.ind,
      query.oc,
      query.op,
      query.ha,
      query.ne,
      query.es
    )
  ) {
    // エリア（全国を除く）
    const { areaName, requestAreaId } = createAreasApiRequest(query, areas);
    targetName = areaName;
    requestParams.areaId = requestAreaId;
    return { requestParams, targetName };
  }
  if (
    isOnlyParam(
      query.pr,
      query.ar,
      query.ci,
      query.ind,
      query.oc,
      query.op,
      query.ha,
      query.ne,
      query.es
    )
  ) {
    // 都道府県（海外を除く）
    const { prefectureName, requestPrefectureId } = createPrefectureApiRequest(
      query,
      areas
    );
    targetName = prefectureName;
    requestParams.prefectureId = requestPrefectureId;
    return { requestParams, targetName };
  }
  if (
    isOnlyParam(
      query.ci,
      query.pr,
      query.ar,
      query.ind,
      query.oc,
      query.op,
      query.ha,
      query.ne,
      query.es
    )
  ) {
    // 政令指定都市（市）
    const { citiesName, requestCitiesId } = createCitiesApiRequest(
      query,
      cities
    );
    targetName = citiesName;
    requestParams.cityId = requestCitiesId;
    return { requestParams, targetName };
  }
  if (
    isOnlyParam(
      query.ind,
      query.ci,
      query.pr,
      query.ar,
      query.oc,
      query.op,
      query.ha,
      query.ne,
      query.es
    )
  ) {
    // 業種
    const { industryName, requestIndustryLId, requestIndustrySId } =
      createIndustryApiRequest(query, industries);
    targetName = industryName;
    if (requestIndustryLId) {
      requestParams.industryLId = [requestIndustryLId];
    } else if (requestIndustrySId) {
      requestParams.industrySId = [requestIndustrySId];
    }
    return { requestParams, targetName };
  }
  if (
    isOnlyParam(
      query.oc,
      query.ind,
      query.ci,
      query.pr,
      query.ar,
      query.op,
      query.ha,
      query.ne,
      query.es
    )
  ) {
    // 職種(一部未使用の職種を除く)
    const {
      occupationName,
      requestOccupationLId,
      requestOccupationMId,
      requestOccupationSId,
    } = createOccupationApiRequest(query, occupations);
    targetName = occupationName;
    if (requestOccupationLId) {
      requestParams.searchJobTypeLId = [requestOccupationLId];
    } else if (requestOccupationMId) {
      requestParams.searchJobTypeMId = [requestOccupationMId];
    } else if (requestOccupationSId) {
      requestParams.searchJobTypeSId = [requestOccupationSId];
    }
    return { requestParams, targetName };
  }
  if (
    isOnlyParam(
      query.op,
      query.oc,
      query.ind,
      query.ci,
      query.pr,
      query.ar,
      query.ha,
      query.ne,
      query.es
    )
  ) {
    // こだわり（op）
    const {
      optionName,
      requestEmploymentStatusId,
      requestHolidayAndWayOfWorkingId,
      requestRequirementsId,
      requestWorkplaceEnvironmentId,
      requestWelfareBenefitsId,
      requestLanguageSkillsId,
      requestPositionsId,
      requestEmployeeAverageAgeId,
    } = createOptionApiRequest(query);
    targetName = optionName;
    if (requestEmploymentStatusId)
      requestParams.employmentStatus = requestEmploymentStatusId;
    if (requestHolidayAndWayOfWorkingId)
      requestParams.holidayAndWayOfWorking = requestHolidayAndWayOfWorkingId;
    if (requestRequirementsId)
      requestParams.requirements = requestRequirementsId;
    if (requestWorkplaceEnvironmentId)
      requestParams.workplaceEnvironment = requestWorkplaceEnvironmentId;
    if (requestWelfareBenefitsId)
      requestParams.welfareBenefits = requestWelfareBenefitsId;
    if (requestLanguageSkillsId)
      requestParams.languageSkills = requestLanguageSkillsId;
    if (requestPositionsId) requestParams.positions = requestPositionsId;
    if (requestEmployeeAverageAgeId)
      requestParams.employeeAverageAge = requestEmployeeAverageAgeId;
    return { requestParams, targetName };
  }
  if (
    isOnlyParamForHa(
      query.ha,
      query.op,
      query.oc,
      query.ind,
      query.ci,
      query.pr,
      query.ar,
      query.ne,
      query.es
    )
  ) {
    // 年収
    const { lowerLimitIncomeName, requestLowerLimitIncomeId } =
      createLowerLimitIncomeApiRequest(query);
    targetName = lowerLimitIncomeName;
    requestParams.lowerLimitIncome = requestLowerLimitIncomeId;
    return { requestParams, targetName };
  }
  if (
    isOnlyParam(
      query.ne,
      query.ha,
      query.op,
      query.oc,
      query.ind,
      query.ci,
      query.pr,
      query.ar,
      query.es
    )
  ) {
    // 従業員数
    const { employNumName, requestEmployNumId } =
      createEmployNumApiRequest(query);
    targetName = employNumName;
    requestParams.employNum = requestEmployNumId;
    return { requestParams, targetName };
  }
  if (
    isOnlyParam(
      query.es,
      query.ne,
      query.ha,
      query.op,
      query.oc,
      query.ind,
      query.ci,
      query.pr,
      query.ar
    )
  ) {
    // 設立
    const { establishName, requestEstablishId } =
      createEstablishApiRequest(query);
    targetName = establishName;
    requestParams.establish = requestEstablishId;
    return { requestParams, targetName };
  }
  throw new Error('パラメータの不正(複数または存在しない)');
};
